import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';
import {
    getAlertContacts, createAlertContact, updateAlertContact, deleteAlertContact,
} from '@/services/api/connections';
import { AlertContact, AlertContactInput } from '../types/meta.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { mensagemDeErro, podeSalvar } from './alertContactsLogic';

const VAZIO: AlertContactInput = { name: '', cell: '' };

export default function AlertContactsList() {
    const [novo, setNovo] = useState<AlertContactInput>(VAZIO);
    const [editando, setEditando] = useState<number | null>(null);
    const [edicao, setEdicao] = useState<AlertContactInput>(VAZIO);
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: contatos = [], isLoading } = useQuery({
        queryKey: ['alert-contacts'],
        queryFn: getAlertContacts,
    });

    const recarregar = () => queryClient.invalidateQueries({ queryKey: ['alert-contacts'] });
    const falhou = (err: unknown) => toast({ title: 'Erro', description: mensagemDeErro(err), variant: 'destructive' });

    const criar = useMutation({
        mutationFn: createAlertContact,
        onSuccess: () => { setNovo(VAZIO); recarregar(); toast({ title: 'Contato adicionado' }); },
        onError: falhou,
    });
    const atualizar = useMutation({
        mutationFn: ({ id, dados }: { id: number; dados: AlertContactInput }) => updateAlertContact(id, dados),
        onSuccess: () => { setEditando(null); recarregar(); toast({ title: 'Contato atualizado' }); },
        onError: falhou,
    });
    const remover = useMutation({
        mutationFn: deleteAlertContact,
        onSuccess: () => { recarregar(); toast({ title: 'Contato removido' }); },
        onError: falhou,
    });

    return (
        <div className="rounded-2xl bg-white p-6 shadow-sm border border-gray-100 space-y-5">
            <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Bell className="h-5 w-5 text-blue-600" /> Alertas
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Quando um perfil ou página sua cair, o alarme no grupo do WhatsApp marca estes números.
                    O número precisa estar no grupo para a marcação funcionar.
                </p>
            </div>

            <form
                className="flex flex-col sm:flex-row gap-2"
                onSubmit={(e) => { e.preventDefault(); if (podeSalvar(novo)) criar.mutate(novo); }}
            >
                <Input placeholder="Nome" value={novo.name} maxLength={120}
                    onChange={(e) => setNovo({ ...novo, name: e.target.value })} />
                <Input placeholder="Celular com DDD, ex: (85) 99999-0000" value={novo.cell} maxLength={30}
                    onChange={(e) => setNovo({ ...novo, cell: e.target.value })} />
                <Button type="submit" disabled={!podeSalvar(novo) || criar.isPending}>
                    {criar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                    Adicionar
                </Button>
            </form>

            {isLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
            ) : contatos.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">Nenhum número cadastrado — o alarme sai sem marcar ninguém.</p>
            ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-500">Nome</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-500">Celular</th>
                            <th className="px-4 py-2 text-right font-medium text-gray-500">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {contatos.map((c: AlertContact) => (
                            <tr key={c.id}>
                                {editando === c.id ? (
                                    <>
                                        <td className="px-4 py-2"><Input value={edicao.name} maxLength={120}
                                            onChange={(e) => setEdicao({ ...edicao, name: e.target.value })} /></td>
                                        <td className="px-4 py-2"><Input value={edicao.cell} maxLength={30}
                                            onChange={(e) => setEdicao({ ...edicao, cell: e.target.value })} /></td>
                                        <td className="px-4 py-2 text-right whitespace-nowrap">
                                            <Button variant="ghost" size="sm" disabled={!podeSalvar(edicao) || atualizar.isPending}
                                                onClick={() => atualizar.mutate({ id: c.id, dados: edicao })}><Check className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => setEditando(null)}><X className="h-4 w-4" /></Button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td className="px-4 py-2 text-gray-900">{c.name}</td>
                                        <td className="px-4 py-2 text-gray-700 font-mono">+{c.cell}</td>
                                        <td className="px-4 py-2 text-right whitespace-nowrap">
                                            <Button variant="ghost" size="sm" title="Editar"
                                                onClick={() => { setEditando(c.id); setEdicao({ name: c.name, cell: c.cell }); }}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" title="Remover" className="text-red-600"
                                                disabled={remover.isPending} onClick={() => remover.mutate(c.id)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
